/**
 * Buscador de clave de cuadro básico (CNIS) — página Mayoreo, pantallas 4a/4b del design doc.
 *
 * Traduce una clave del Compendio Nacional de Insumos para la Salud (`GGG.SSS.NNNN`) al producto
 * equivalente del catálogo. El mapeo vive en `assets/claves-cuadro-basico.json`, que se genera con
 * `scripts/shopify/build-claves-cuadro-basico.js` y se puede actualizar sin tocar este archivo.
 *
 * Decisiones que no son obvias al leer el código:
 *
 * · El JSON se pide en el PRIMER foco del input, no en el load. La mayoría de quien entra a Mayoreo
 *   no usa el buscador y no tiene por qué pagarlo (2026-08-19: 104 KB, ~10 KB por la red).
 *
 * · El precio y la imagen NO están en el JSON: se piden a `/products/{handle}.js` en el momento. Un
 *   precio congelado en un asset caduca en cuanto cambia el catálogo y ahí sí engaña.
 *
 * · Se muestra el precio de LA VARIANTE que corresponde a la clave, no el del producto. El endpoint
 *   `.js` devuelve el mínimo del producto y en 42 fichas ese mínimo es el de la caja: mostrarlo haría
 *   ver la presentación equivocada. Ver `memory/precio_de_caja_falsea_el_snippet.md`.
 *
 * · No se dice nada de existencias. El surtido es sobre pedido y el inventario de Shopify no
 *   describe lo que el proveedor puede conseguir. Ver `memory/stock_es_dropshipping.md`.
 *
 * · WhatsApp es la salida de TODOS los estados, incluido el error de red: el buscador puede fallar,
 *   la cotización no.
 */
(function () {
  'use strict';

  var soloDigitos = function (valor) {
    return String(valor || '').replace(/\D/g, '');
  };

  /** `0602030306` → `060.203.0306`. Es como el usuario la lee en su receta. */
  function formatearClave(digitos) {
    if (digitos.length === 12) {
      return digitos.slice(0, 3) + '.' + digitos.slice(3, 6) + '.' + digitos.slice(6, 10) + '.' + digitos.slice(10);
    }
    if (digitos.length === 10) {
      return digitos.slice(0, 3) + '.' + digitos.slice(3, 6) + '.' + digitos.slice(6);
    }
    return digitos;
  }

  var formatoMXN;
  function precio(centavos) {
    if (typeof centavos !== 'number') return '';
    if (!formatoMXN) {
      try {
        formatoMXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
      } catch (e) {
        formatoMXN = { format: function (n) { return '$' + n.toFixed(2); } };
      }
    }
    return formatoMXN.format(centavos / 100);
  }

  /** Empuja el evento a dataLayer. El tag de Google carga diferido, así que no se llama a gtag(). */
  function medir(nombre, datos) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: nombre }, datos || {}));
    } catch (e) { /* la medición nunca puede romper el buscador */ }
  }

  function waUrl(telefono, mensaje) {
    return 'https://wa.me/' + telefono + '?text=' + encodeURIComponent(mensaje);
  }

  function el(tag, clase, texto) {
    var nodo = document.createElement(tag);
    if (clase) nodo.className = clase;
    if (texto != null) nodo.textContent = texto;
    return nodo;
  }

  function iniciar(raiz) {
    if (raiz.dataset.smCbReady === '1') return;
    raiz.dataset.smCbReady = '1';

    var form = raiz.querySelector('[data-sm-cb-form]');
    var input = raiz.querySelector('[data-sm-cb-input]');
    var resultados = raiz.querySelector('[data-sm-cb-results]');
    var telefono = raiz.dataset.smCbPhone;
    var src = raiz.dataset.smCbSrc;
    if (!form || !input || !resultados || !telefono || !src) return;

    var datos = null;
    var cargando = null;
    var cacheProducto = {};

    function cargar() {
      if (datos) return Promise.resolve(datos);
      if (cargando) return cargando;
      cargando = fetch(src)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (json) {
          datos = json;
          // El `version` en consola es lo que soporte le pide al usuario para saber qué lote de
          // claves está viendo el navegador (criterio de aceptación 5 del spec).
          if (window.console) console.info('[cuadro básico] claves ' + json.version + ' · ' + json.claves.length + ' registros');
          return json;
        })
        .catch(function (err) {
          cargando = null;
          throw err;
        });
      return cargando;
    }

    function producto(handle) {
      if (cacheProducto[handle]) return cacheProducto[handle];
      cacheProducto[handle] = fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'products/' + handle + '.js'
        : '/products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
      return cacheProducto[handle];
    }

    /**
     * La variante que corresponde a la clave. El SKU puede repetirse entre la variante de caja y la
     * de pieza suelta dentro del MISMO producto, así que se desempata con el título que registró el
     * cruce. Ver `memory/shopify_sku_duplicado_entre_variantes.md`.
     */
    function elegirVariante(prod, ref) {
      if (!prod || !prod.variants || !prod.variants.length) return null;
      var porSkuYTitulo = prod.variants.filter(function (v) {
        return v.sku === ref.sku && ref.variante && v.title === ref.variante;
      });
      if (porSkuYTitulo.length) return porSkuYTitulo[0];
      var porSku = prod.variants.filter(function (v) { return v.sku === ref.sku; });
      if (porSku.length) return porSku[0];
      return prod.variants[0];
    }

    function limpiar() {
      while (resultados.firstChild) resultados.removeChild(resultados.firstChild);
    }

    function aviso(clase, titulo, cuerpo) {
      limpiar();
      var caja = el('div', 'sm-cb__note ' + clase);
      caja.appendChild(el('p', 'sm-cb__note-title', titulo));
      if (cuerpo) caja.appendChild(el('p', 'sm-cb__note-body', cuerpo));
      resultados.appendChild(caja);
      return caja;
    }

    function ctaWhatsApp(caja, mensaje, contexto, clave) {
      var a = el('a', 'sm-cb__note-cta', 'Cotizar por WhatsApp');
      a.href = waUrl(telefono, mensaje);
      a.target = '_blank';
      a.rel = 'noopener';
      a.addEventListener('click', function () {
        medir('cb_whatsapp_click', { cb_clave: clave || '', cb_contexto: contexto });
      });
      caja.appendChild(a);
    }

    function tarjeta(ref, claveFmt, descripcion) {
      var card = el('article', 'sm-cb__card');
      card.dataset.smCbCard = ref.handle;

      var media = el('div', 'sm-cb__card-media');
      card.appendChild(media);

      var cuerpo = el('div', 'sm-cb__card-body');
      var titulo = el('a', 'sm-cb__card-title', ref.titulo);
      titulo.href = '/products/' + ref.handle;
      cuerpo.appendChild(titulo);
      if (ref.variante) cuerpo.appendChild(el('p', 'sm-cb__card-variant', ref.variante));
      if (descripcion) cuerpo.appendChild(el('p', 'sm-cb__card-official', descripcion));
      var precioEl = el('p', 'sm-cb__card-price', '');
      cuerpo.appendChild(precioEl);

      var acciones = el('div', 'sm-cb__card-actions');
      var ver = el('a', 'sm-cb__card-link', 'Ver el producto');
      ver.href = '/products/' + ref.handle;
      acciones.appendChild(ver);

      var wa = el('a', 'sm-cb__card-wa', 'Cotizar por WhatsApp');
      wa.href = waUrl(telefono, 'Hola, quiero cotizar la clave ' + claveFmt + ' (' + ref.titulo + (ref.variante ? ' — ' + ref.variante : '') + ')');
      wa.target = '_blank';
      wa.rel = 'noopener';
      wa.addEventListener('click', function () {
        medir('cb_whatsapp_click', { cb_clave: claveFmt, cb_contexto: 'match' });
      });
      acciones.appendChild(wa);
      cuerpo.appendChild(acciones);
      card.appendChild(cuerpo);

      // Foto, precio y URL con la variante correcta llegan después: la tarjeta ya es útil sin ellos.
      producto(ref.handle).then(function (prod) {
        if (!prod) return;
        var variante = elegirVariante(prod, ref);
        var img = (variante && variante.featured_image && variante.featured_image.src) || prod.featured_image;
        if (img) {
          var imagen = el('img');
          imagen.src = img.indexOf('//') === 0 ? 'https:' + img : img;
          imagen.alt = prod.title;
          imagen.loading = 'lazy';
          media.appendChild(imagen);
        }
        if (variante) {
          if (typeof variante.price === 'number') precioEl.textContent = precio(variante.price);
          var url = '/products/' + ref.handle + '?variant=' + variante.id;
          titulo.href = url;
          ver.href = url;
        }
      });

      return card;
    }

    function pintarMatch(registros, claveFmt) {
      limpiar();

      var unaSola = registros.length === 1;
      resultados.appendChild(el('p', 'sm-cb__found', unaSola
        ? (registros[0].productos.length === 1
            ? 'Encontramos el equivalente de la clave ' + (registros[0].clave_formateada || claveFmt) + ':'
            : 'Encontramos estos equivalentes de la clave ' + (registros[0].clave_formateada || claveFmt) + ':')
        : 'Hay ' + registros.length + ' claves que empiezan por ' + claveFmt + ':'));

      registros.forEach(function (reg) {
        // Con varias claves en pantalla, cada grupo lleva su encabezado: si no, las tarjetas se
        // mezclan y no hay forma de saber cuál corresponde a cuál clave.
        if (!unaSola) {
          var titulo = el('p', 'sm-cb__group');
          titulo.appendChild(el('span', 'sm-cb__group-clave', reg.clave_formateada));
          if (reg.descripcion_oficial) titulo.appendChild(el('span', 'sm-cb__group-desc', reg.descripcion_oficial));
          resultados.appendChild(titulo);
        }
        var lista = el('div', 'sm-cb__cards');
        reg.productos.forEach(function (ref) {
          lista.appendChild(tarjeta(ref, reg.clave_formateada || claveFmt, unaSola ? reg.descripcion_oficial : ''));
        });
        resultados.appendChild(lista);
      });
    }

    function buscar() {
      var q = soloDigitos(input.value);
      var claveFmt = formatearClave(q);

      if (!q.length) {
        limpiar();
        return;
      }

      medir('cb_search', { cb_clave: claveFmt, cb_digitos: q.length });

      if (q.length < 7) {
        aviso('sm-cb__note--hint', 'Ingresa la clave completa', 'Por ejemplo 060.168.0409. Van los 10 dígitos, con o sin puntos.');
        return;
      }

      aviso('sm-cb__note--loading', 'Buscando…');

      cargar().then(function (json) {
        var exacta = null;
        var parciales = [];
        for (var i = 0; i < json.claves.length; i++) {
          var c = json.claves[i];
          if (c.clave === q) { exacta = c; break; }
          if (c.clave.indexOf(q) === 0) parciales.push(c);
        }

        if (exacta) {
          medir('cb_match', { cb_clave: claveFmt });
          pintarMatch([exacta], claveFmt);
          return;
        }
        if (parciales.length) {
          medir('cb_partial', { cb_clave: claveFmt, cb_resultados: parciales.length });
          pintarMatch(parciales.slice(0, 5), claveFmt);
          return;
        }

        medir('cb_no_match', { cb_clave: claveFmt });
        var caja = aviso(
          'sm-cb__note--empty',
          'No encontramos la clave ' + claveFmt,
          'Puede que la surtamos igual: el catálogo mapeado aquí es solo una parte. Mándanosla y te la cotizamos.'
        );
        ctaWhatsApp(caja, 'Hola, quiero cotizar la clave de cuadro básico: ' + claveFmt, 'no_match', claveFmt);
      }).catch(function () {
        var caja = aviso(
          'sm-cb__note--empty',
          'No pudimos consultar el catálogo de claves',
          'Mándanos la clave por WhatsApp y te cotizamos igual.'
        );
        ctaWhatsApp(caja, 'Hola, quiero cotizar la clave de cuadro básico: ' + claveFmt, 'error', claveFmt);
      });
    }

    input.addEventListener('focus', function () { cargar().catch(function () {}); }, { once: true });
    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      buscar();
    });

    var directo = raiz.querySelector('[data-sm-cb-wa]');
    if (directo) {
      directo.addEventListener('click', function () {
        medir('cb_whatsapp_click', { cb_clave: formatearClave(soloDigitos(input.value)), cb_contexto: 'directo' });
      });
    }
  }

  function arrancar() {
    var nodos = document.querySelectorAll('[data-sm-cb]');
    for (var i = 0; i < nodos.length; i++) iniciar(nodos[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
  // El editor del tema repinta la sección sin recargar la página.
  document.addEventListener('shopify:section:load', arrancar);
})();
